import {useQuery} from "@tanstack/react-query";
import {OverwolfApi} from "@devour/client";
import getConfig from "@/utils/getConfig";

/*
 * Builds a name → icon-URL lookup for every Overwolf-tracked game.
 * The competition-games hook only returns competition-supported games
 * (Fortnite, Apex), so a Twitch directory entry like "Call of Duty:
 * Black Ops 7" never finds its icon there. This hook pulls the FULL game
 * catalog via the dedicated `/overwolf/game-icons` endpoint, which returns
 * every game that has an icon with no row cap (unlike enhanced-games/search,
 * which is capped at 100 and starved most games of icons) so the sub-rail /
 * collapsed FilterRail can render brand icons instead of letter glyphs.
 *
 * Field names: `OWGameResponse` carries `iconUrl`, `thumbnailImageUrl`,
 * and `bannerImageSmallUrl` — NOT `thumbnailUrl` or `smallBannerUrl`.
 * Reading typed fields directly (no cast) keeps the field names honest.
 */
export function useAllGameIcons() {
    return useQuery({
        queryKey: ["drop-in-all-game-icons"],
        queryFn: async () => {
            const api = new OverwolfApi(getConfig(null));
            /*
             * Dedicated all-games icon endpoint — name→iconUrl for the FULL
             * catalog. The old enhanced-games/search path capped at 100 rows, so
             * games ranked below the top-100 (Marvel Rivals, CS2, …) never got an
             * icon and fell back to a letter glyph.
             *
             * Guard against a stale local @devour/client/dist that predates this
             * endpoint (the method is absent → "not a function"). Fall back to an
             * empty map (letter glyphs) rather than erroring the query.
             */
            const fn = (api as unknown as {
                getOverwolfGameIcons?: () => Promise<{ games?: Array<{ name?: string; iconUrl?: string }> }>;
            }).getOverwolfGameIcons;
            if (typeof fn !== "function") {
                return new Map<string, string>();
            }
            const data = await fn.call(api);
            const map = new Map<string, string>();
            for (const g of data.games ?? []) {
                if (g.name && g.iconUrl) map.set(g.name.toLowerCase(), g.iconUrl);
            }
            return map;
        },
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 7 * 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}
